import { useApiQuery } from '../useApiQuery';
import { apiGet, operationsClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import { fastestPollInterval, getEmailDeliveryPollInterval } from '@/lib/email-delivery';
import { receiptKeys } from '@/lib/queryKeys';
import type { ReceiptDetail } from './types';

/**
 * Refetch every 4 s while the newest email — the receipt's, or its payment's
 * bank-authorization copy — is QUEUED, for up to 3 minutes.
 */
function pollWhileEmailQueued(query: { state: { data?: ReceiptDetail } }): number | false {
  const receipt = query.state.data?.receipt;
  return fastestPollInterval(
    getEmailDeliveryPollInterval(receipt?.emailDelivery),
    getEmailDeliveryPollInterval(receipt?.achAuthorizationCopy),
  );
}

export function useReceipt(id: string | undefined) {
  return useApiQuery<ReceiptDetail>({
    queryKey: receiptKeys.detail(id),
    enabled: Boolean(id),
    queryFn: () => apiGet<ReceiptDetail>(operationsClient, endpoints.receipts.detail(id!)),
    refetchInterval: pollWhileEmailQueued,
  });
}

export function useReceiptForPayment(paymentId: string | undefined) {
  return useApiQuery<ReceiptDetail>({
    queryKey: receiptKeys.byPayment(paymentId),
    enabled: Boolean(paymentId),
    queryFn: () =>
      apiGet<ReceiptDetail>(operationsClient, endpoints.receipts.forPayment(paymentId!)),
    showErrorToast: false,
    refetchInterval: pollWhileEmailQueued,
  });
}

export function useReceiptForDeposit(depositId: string | undefined) {
  return useApiQuery<ReceiptDetail>({
    queryKey: receiptKeys.byDeposit(depositId),
    enabled: Boolean(depositId),
    queryFn: () =>
      apiGet<ReceiptDetail>(operationsClient, endpoints.receipts.forDeposit(depositId!)),
    showErrorToast: false,
    refetchInterval: pollWhileEmailQueued,
  });
}
